import React, { useState, useEffect, useContext, useRef } from 'react';
import { UniverseBuilder } from '@baemingo/shopping-cart-events';
import { useSelector, useDispatch, useLogger, useCurrency } from 'Hooks';
import { v4 as uuid } from 'uuid';
import Config from 'config';
import ReactGA from 'react-ga4';
import * as actions from 'actions';
import { gql, useQuery } from '@apollo/client';
import { AvailableOffer } from '@baemingo/shopping-cart-events/dist/types';

/**
 * Create a context with an empty universe. This context acts as a
 * state that we can access from child components. We use this in app.tsx
 */
const Context = React.createContext(new UniverseBuilder().serialize());

export const ShoppingCartUniverseProvider: React.FunctionComponent = ({ children }) => {
  const logger = useLogger('shopping-cart-universe-provider');
  // A local universe
  const universe = useRef(new UniverseBuilder());

  // Get events from state. Events are added via UI actions or synced from backend
  const events = useSelector((state) => state.shoppingCartEvents);

  const cartId = useSelector((state) => state.cartId);

  // We save the serialized universe in state to trigger re-renders
  const [state, setState] = useState(universe.current.serialize());

  useEffect(() => {
    universe.current = new UniverseBuilder();

    events.forEach((event) => {
      // Basic sanity check
      if (!event.venueId) {
        console.error('Event without venueId: ' + event.type);
      }

      try {
        universe.current.addEvent(event);
      } catch (e) {
        console.error(e);
      }
    });
    logger.info(`Incoming ShoppingCartEvent: ${event}`);
    console.log(universe.current.serialize());

    setState(universe.current.serialize());
  }, [events]);

  useEffect(() => {
    if (!cartId) {
      universe.current = new UniverseBuilder();
    }
  }, [cartId]);

  return <Context.Provider value={state}>{children}</Context.Provider>;
};

export const useShoppingCart = () => {
  return useContext(Context);
};

export const ITEM_VATRATES = gql`
  query GetItemVatRates($companyId: ID!) {
    items: allItems(filter: { company: { id: $companyId } }) {
      id
      vatRate
    }
  }
`;
export const useShoppingCartApi = () => {
  const dispatch = useDispatch();
  const logger = useLogger('shopping-cart-api');
  const cartId = useSelector((state) => state.cartId!);
  const user = useSelector((state) => state.user);
  const membershipRate = useSelector((state) => state.userMembership?.rate ?? 0);
  const venueId = useSelector((state) => state.venue?.id ?? '');
  const { formatAmount } = useCurrency();

  const itemsQuery = useQuery(ITEM_VATRATES, {
    variables: {
      companyId: Config.COMPANY_ID,
    },
  });

  const items = itemsQuery.data?.items ?? [];

  const generateEventMeta = () => ({
    createdAt: new Date().toISOString(),
    eventId: uuid(),
    sentAt: null,
    cartId,
    venueId,
  });

  const API = {
    createCart: (
      venueId: string,
      serviceType: 'TAKE_AWAY' | 'EAT_HERE' | 'DELIVERY',
      deliveryTargetId: string | null,
    ) => {
      const id = uuid();

      dispatch(actions.setCartId(id));

      setTimeout(() => {
        dispatch(
          actions.addShoppingCartEvent({
            type: 'CREATE_CART',
            deliveryTargetId,
            serviceType,
            userId: user?.id,
            ...generateEventMeta(),
            cartId: id,
            venueId,
          }),
        );
        logger.info('Sending CREATE_CART event');

        dispatch(
          actions.addShoppingCartEvent({
            type: 'SET_MEMBERSHIP_DISCOUNT',
            discountRate: membershipRate,
            ...generateEventMeta(),
            venueId,
            cartId: id,
          }),
        );
        logger.info(`Setting membership discount on cart: ${cartId}`);
      }, 10);

      // Trigger GA event that a cart is created.
      ReactGA.event({
        category: 'Cart',
        action: 'cart_created',
      });

      return id;
    },
    addItem: (
      title: string,
      itemId: string,
      entryId: string,
      variations: Array<{
        id: string;
        amount: number;
        title: string;
        groupId: string;
      }>,
      removedVariations: string[],
      amount: number,
      disableMembershipDiscounts: boolean,
      vatRate?: number,
    ) => {
      const findVatRate = items.find((i: any) => i.id === itemId)?.vatRate;

      if (!findVatRate) {
        logger.error('Missing vat rate, ignoring add');
        return;
      }

      dispatch(
        actions.addShoppingCartEvent({
          type: 'ADD_ITEM',
          item: {
            id: uuid(),
            disableMembershipDiscounts,
            discount: 0,
            itemId,
            entryId,
            vatRate: vatRate ?? findVatRate,
            message: null,
            amount,
            variations,
            removedVariations,
            title,
          },
          ...generateEventMeta(),
        }),
      );
      logger.info(`Adding item to cart: ${cartId}`);
    },
    addItems: (
      itemsArray: Array<{
        title: string;
        itemId: string;
        entryId: string;
        variations: Array<{
          id: string;
          amount: number;
          title: string;
          groupId: string;
        }>;
        removedVariations: string[];
        amount: number;
        disableMembershipDiscounts: boolean;
        discount: number;
        vatRate?: number;
      }>,
    ) => {
      const vatRate = items.find((i: any) => i.id === itemsArray[0].itemId)?.vatRate;
      if (!vatRate) {
        logger.error('Missing vat rate, ignoring add');
        return;
      }

      dispatch(
        actions.addShoppingCartEvent({
          type: 'ADD_ITEMS',
          items: itemsArray.map((item) => ({
            ...item,
            id: uuid(),
            vatRate: vatRate,
            message: null,
          })),
          ...generateEventMeta(),
        }),
      );
      logger.info(`Adding items to cart: ${cartId}`);
    },
    clearCart: () => {
      dispatch(
        actions.addShoppingCartEvent({
          type: 'CLEAR_CART',
          ...generateEventMeta(),
        }),
      );
      logger.info(`Clearing cart: ${cartId}`);
    },
    removeItem: (id: string) => {
      dispatch(
        actions.addShoppingCartEvent({
          type: 'REMOVE_ITEM',
          id,
          ...generateEventMeta(),
        }),
      );
      logger.info(`Removing item: ${id} from cart: ${cartId}`);
    },
    setComment: (comment: string) => {
      dispatch(
        actions.addShoppingCartEvent({
          type: 'SET_COMMENT',
          comment,
          ...generateEventMeta(),
        }),
      );
      logger.info(`Setting comment on cart: ${cartId}`);
    },
    setPickupTime: (time: string) => {
      dispatch(
        actions.addShoppingCartEvent({
          type: 'SET_PICKUP_TIME',
          time,
          ...generateEventMeta(),
        }),
      );
      logger.info(`Setting pickup time on cart: ${cartId}`);
    },
    setOrderOffer: (id: string, title: string, discountRate: number, discountType: any) => {
      dispatch(
        actions.addShoppingCartEvent({
          type: 'SET_ORDER_OFFER',
          id,
          title,
          discountRate,
          discountType,
          ...generateEventMeta(),
        }),
      );
      logger.info(`Setting Order Offer: ${id} on cart: ${cartId}`);
    },
    clearOrderOffer: () => {
      dispatch(
        actions.addShoppingCartEvent({
          type: 'CLEAR_ORDER_OFFER',
          ...generateEventMeta(),
        }),
      );
      logger.info(`Clearing Order Offer on cart: ${cartId}`);
    },
    setMembershipDiscount: (discountRate: number) => {
      dispatch(
        actions.addShoppingCartEvent({
          type: 'SET_MEMBERSHIP_DISCOUNT',
          discountRate,
          ...generateEventMeta(),
        }),
      );
      logger.info(`Setting membership discount on cart: ${cartId}`);
    },
    updateCartUser: (id: string) => {
      dispatch(
        actions.addShoppingCartEvent({
          type: 'UPDATE_USER',
          id,
          ...generateEventMeta(),
        }),
      );
      logger.info(`Updating user: ${id} on cart: ${cartId}`);
    },
    addAvailableOffer: (offer: AvailableOffer) => {
      dispatch(
        actions.addShoppingCartEvent({
          type: 'ADD_OFFER',
          ...offer,
          ...generateEventMeta(),
        }),
      );
      logger.info(`Adding offer: ${offer.id} to cart: ${cartId}`);
    },
    addClientSideInvoicePayment: (amount: number, tip: number) => {
      dispatch(
        actions.addShoppingCartEvent({
          type: 'ADD_CLIENT_SIDE_PAYMENT',
          ...generateEventMeta(),
          id: uuid(),
          tip,
          paymentType: 'INVOICE',
          transactionId: '',
          amount,
          payload: '',
        }),
      );
      logger.info(`Creating invoice payment. Amount: ${formatAmount(amount)}`, { cartId });
    },
    addClientSidePayment: () => {
      dispatch(
        actions.addShoppingCartEvent({
          type: 'ADD_CLIENT_SIDE_PAYMENT',
          ...generateEventMeta(),
          id: uuid(),
          tip: 0,
          paymentType: 'ADYEN',
          transactionId: '',
          amount: 0,
          payload: '',
        }),
      );
      logger.info(`Adding client side payment to cart: ${cartId}`);
    },
  };

  return API;
};
